import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import NavigationUser from '../Layouts/Navigation/NavigationUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faBox, 
  faUserGroup, 
  faCar,
  faChartLine,
  faCheckCircle,
  faHourglass,
  faTruck
} from '@fortawesome/free-solid-svg-icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { BASE_URL } from '../utils/constants';

function IndexUser() {
  const [packageStats, setPackageStats] = useState({ 
    total: 0, 
    entregados: 0, 
    pendientes: 0 
  });
  const [visitStats, setVisitStats] = useState({ 
    total: 0, 
    completadas: 0, 
    pendientes: 0 
  });
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Obtener userId de sessionStorage o del token JWT
  const getUserId = () => {
    let userId = sessionStorage.getItem('id_usuario');
    
    // Si no está en sessionStorage, intentar obtenerlo del token
    if (!userId) {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const decodedToken = jwtDecode(token);
          userId = decodedToken.id_usuario;
          // Guardar en sessionStorage para futuras consultas
          sessionStorage.setItem('id_usuario', userId);
          console.log('ID de usuario extraído del token:', userId);
        } catch (error) {
          console.error('Error al decodificar el token:', error);
          navigate('/');
          return null;
        }
      } else {
        console.error('No hay token en localStorage');
        navigate('/');
        return null;
      }
    }
    
    return userId;
  };

  const userId = getUserId();

  useEffect(() => {
    console.log('=== DASHBOARD INICIADO ===');
    console.log('User ID:', userId);
    console.log('BASE_URL:', BASE_URL);
    if (userId) {
      fetchData();
    } else {
      console.error('No hay usuario en sessionStorage');
      setError('No se encontró el ID de usuario');
      setLoading(false);
    }
  }, [userId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('=== INICIANDO PETICIONES API ===');
      
      // Obtener estadísticas de paquetes
      const packagesUrl = `${BASE_URL}/getPaqueteriaByIdInquilino/${userId}`;
      console.log('1. Obteniendo paquetes de:', packagesUrl);
      const packagesRes = await axios.get(packagesUrl);
      console.log('✓ Paquetes obtenidos:', packagesRes.data);
      const packages = packagesRes.data;
      const packageData = {
        total: packages.length,
        entregados: packages.filter(p => p.estado === 'Entregado').length,
        pendientes: packages.filter(p => p.estado === 'Pendiente').length
      };
      console.log('Estadísticas de paquetes:', packageData);
      setPackageStats(packageData);

      // Obtener estadísticas de visitas
      const visitsUrl = `${BASE_URL}/getVisitsByUser/${userId}`;
      console.log('2. Obteniendo visitas de:', visitsUrl);
      const visitsRes = await axios.get(visitsUrl);
      console.log('✓ Visitas obtenidas:', visitsRes.data);
      const visits = visitsRes.data;
      const visitData = {
        total: visits.length,
        completadas: visits.filter(v => v.flag_ingreso === 1).length,
        pendientes: visits.filter(v => v.flag_ingreso === 0 || v.flag_ingreso === 2).length
      };
      console.log('Estadísticas de visitas:', visitData);
      setVisitStats(visitData);

      // Obtener vehículos registrados
      const vehiclesUrl = `${BASE_URL}/vehicles/user/${userId}`;
      console.log('3. Obteniendo vehículos de:', vehiclesUrl);
      const vehiclesRes = await axios.get(vehiclesUrl);
      console.log('✓ Vehículos obtenidos:', vehiclesRes.data);
      setVehicles(vehiclesRes.data);

      console.log('=== TODAS LAS PETICIONES COMPLETADAS ===');
    } catch (error) {
      console.error('❌ ERROR EN DASHBOARD:', error);
      console.error('Error completo:', error.response || error);
      if (error.response) {
        console.error('Status:', error.response.status);
        console.error('Data:', error.response.data);
        console.error('Headers:', error.response.headers);
        setError(`Error ${error.response.status}: ${error.response.data?.message || 'Error del servidor'}`);
      } else if (error.request) {
        console.error('No se recibió respuesta:', error.request);
        setError('No se pudo conectar con el servidor. Verifica que el backend esté ejecutándose.');
      } else {
        console.error('Error:', error.message);
        setError(`Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const packageChartData = [
    { name: 'Entregados', value: packageStats.entregados, color: '#10b981' },
    { name: 'Pendientes', value: packageStats.pendientes, color: '#f59e0b' }
  ];

  const visitChartData = [
    { name: 'Completadas', value: visitStats.completadas, color: '#3b82f6' },
    { name: 'Pendientes', value: visitStats.pendientes, color: '#ef4444' }
  ];

  const COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#ef4444'];

  if (loading) {
    return (
      <>
        <NavigationUser/>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando dashboard...</p>
            <p className="mt-2 text-sm text-gray-500">Revisa la consola del navegador (F12) para ver el progreso</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <NavigationUser/>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
            <div className="text-center">
              <div className="text-red-500 text-5xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Error al cargar el dashboard</h2>
              <p className="text-red-600 mb-6">{error}</p>
              <button 
                onClick={fetchData}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
              >
                Reintentar
              </button>
              <p className="mt-4 text-sm text-gray-500">
                Abre la consola del navegador (F12) para más detalles
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <NavigationUser/>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl shadow-lg">
                <FontAwesomeIcon icon={faChartLine} className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Mi Dashboard</h1>
                <p className="text-gray-600">Resumen de tu actividad en el conjunto</p>
              </div>
            </div>
          </div>

          {/* Estadísticas Rápidas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Paquetes */}
            <div 
              onClick={() => navigate('/ShipmentsUsrReport')}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all cursor-pointer transform hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Paquetes</p>
                  <p className="text-3xl font-bold text-gray-800">{packageStats.total}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <FontAwesomeIcon icon={faBox} className="h-8 w-8 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Click para ver reporte</p>
            </div>

            {/* Paquetes Pendientes */}
            <div 
              onClick={() => navigate('/ShipmentsUsrReport')}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all cursor-pointer transform hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Paquetes Pendientes</p>
                  <p className="text-3xl font-bold text-amber-600">{packageStats.pendientes}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-lg">
                  <FontAwesomeIcon icon={faHourglass} className="h-8 w-8 text-amber-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Click para ver reporte</p>
            </div>

            {/* Total Visitas */}
            <div 
              onClick={() => navigate('/VisitsUsrReport')}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all cursor-pointer transform hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Visitas</p>
                  <p className="text-3xl font-bold text-gray-800">{visitStats.total}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FontAwesomeIcon icon={faUserGroup} className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Click para ver reporte</p>
            </div>

            {/* Visitas Pendientes */}
            <div 
              onClick={() => navigate('/VisitsUsrReport')}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all cursor-pointer transform hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Visitas Pendientes</p>
                  <p className="text-3xl font-bold text-red-600">{visitStats.pendientes}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <FontAwesomeIcon icon={faHourglass} className="h-8 w-8 text-red-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Click para ver pendientes</p>
            </div>
          </div>

          {/* Gráficas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Gráfica de Paquetes */}
            <div 
              onClick={() => navigate('/ShipmentsUsrReport')}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all cursor-pointer"
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faBox} className="text-purple-600" />
                Estado de Paquetes
                <span className="text-xs font-normal text-gray-500 ml-auto">Click para ver reporte</span>
              </h2>
              {packageStats.total > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={packageChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {packageChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12">
                  <FontAwesomeIcon icon={faBox} className="h-16 w-16 text-gray-300 mb-4" />
                  <p className="text-gray-500">No hay paquetes registrados</p>
                </div>
              )}
            </div>

            {/* Gráfica de Visitas */}
            <div 
              onClick={() => navigate('/VisitsUsrReport')}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all cursor-pointer"
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faUserGroup} className="text-blue-600" />
                Estado de Visitas
                <span className="text-xs font-normal text-gray-500 ml-auto">Click para ver reporte</span>
              </h2>
              {visitStats.total > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={visitChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {visitChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12">
                  <FontAwesomeIcon icon={faUserGroup} className="h-16 w-16 text-gray-300 mb-4" />
                  <p className="text-gray-500">No hay visitas registradas</p>
                </div>
              )}
            </div>
          </div>

          {/* Vehículos Registrados */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faCar} className="text-green-600" />
                Vehículos del Apartamento
              </h2>
              <button
                onClick={() => navigate('/UserVehicles')}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-semibold"
              >
                Ver Detalles
              </button>
            </div>
            {vehicles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {vehicles.map((vehicle, index) => (
                  <div 
                    key={index} 
                    onClick={() => navigate('/UserVehicles')}
                    className="border-2 border-gray-200 rounded-lg p-5 hover:shadow-lg hover:border-green-500 transition-all cursor-pointer transform hover:scale-105"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md">
                        <FontAwesomeIcon icon={faCar} className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-xl text-gray-800">{vehicle.placa}</p>
                        <p className="text-sm text-green-600 font-semibold">
                          {vehicle.torre_nombre || 'Sin torre asignada'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Ubicación de Parqueadero</span>
                        <span className="text-lg font-bold text-green-600">
                          #{vehicle.num_parqueadero || 'N/A'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm border-b border-gray-100 pb-2">
                        <span className="text-gray-600">Propietario:</span>
                        <span className="font-semibold text-gray-800">{vehicle.usuario_nombre} {vehicle.usuario_apellido}</span>
                      </div>
                      <div className="flex justify-between text-sm border-b border-gray-100 pb-2">
                        <span className="text-gray-600">Marca:</span>
                        <span className="font-semibold text-gray-800">{vehicle.marca}</span>
                      </div>
                      <div className="flex justify-between text-sm border-b border-gray-100 pb-2">
                        <span className="text-gray-600">Modelo:</span>
                        <span className="font-semibold text-gray-800">{vehicle.modelo}</span>
                      </div>
                      {vehicle.telefono && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Teléfono:</span>
                          <span className="font-semibold text-blue-600">{vehicle.telefono}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-200 text-center">
                      <span className="text-xs text-gray-500">Click para ver detalles</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div 
                onClick={() => navigate('/UserVehicles')}
                className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg cursor-pointer hover:shadow-md transition-all"
              >
                <FontAwesomeIcon icon={faCar} className="h-16 w-16 text-gray-300 mb-4" />
                <p className="text-gray-500 mb-2 font-semibold">No hay vehículos registrados en el apartamento</p>
                <p className="text-sm text-gray-400">Comunícate con administración para registrar vehículos</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default IndexUser